import { useSearchParams } from 'react-router-dom';

import { messagingSections } from '@/modules/messaging/constants';
import {
  useMessagingActions,
  useSelectedMessageIds,
} from '@/modules/messaging/messagingStore';
import { messagingQueryKeys } from '@/modules/messaging/queries/queryKeys';
import { useMessagesQuery } from '@/modules/messaging/queries/useMessagesQuery';
import type { MessageWithThreadApi } from '@/modules/messaging/types';
import { useSearchKeyword } from '@/modules/search/hooks/useSearchKeyword';
import { CardEmptyState } from '@/modules/ui/components/card-empty-state/CardEmptyState';
import { Input } from '@/modules/ui/components/input/Input';
import Loader from '@/modules/ui/components/loader/Loader';
import { invalidateQuery } from '@/utils/queries';

import { MessageList } from './messageThread/MessageList';
import { ThreadHeader } from './messageThread/ThreadHeader';
import { MessagingLayout } from './MessagingLayout';
import {
  type SelectItemPayload,
  ThreadListItem,
} from './threadList/ThreadListItem';

const DRAFT_MESSAGE_STATUS = ['Draft' as const];

export const DraftMessages = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const highlightedMessageQueryParam = searchParams.get('messageId') ?? null;
  const tab = searchParams.get('tab') ?? messagingSections.drafts;

  const { searchKeyword, onChange, filterItemsBySearchKeyword } =
    useSearchKeyword();

  const compareFn = (message: MessageWithThreadApi) =>
    filterItemsBySearchKeyword(
      message,
      (message) => message.thread.candidate?.firstName,
    ) ||
    filterItemsBySearchKeyword(
      message,
      (message) => message.thread.candidate?.lastName,
    ) ||
    filterItemsBySearchKeyword(message, (message) =>
      `${message.thread.candidate?.firstName} ${message.thread.candidate?.lastName}`.trim(),
    );

  const messagesQuery = useMessagesQuery({ statuses: DRAFT_MESSAGE_STATUS });

  const filteredMessages = messagesQuery.data?.filter(compareFn);

  const selectedMessageIds = useSelectedMessageIds();
  const { selectMessage, selectMessages, unselectMessage, clearMessages } =
    useMessagingActions();

  const highlightedMessageId =
    highlightedMessageQueryParam ?? filteredMessages?.at(0)?.id;

  const highlightedMessage = filteredMessages?.find(
    (message) => message.id === highlightedMessageId,
  );

  const handleSelectMessage = ({ id, checked }: SelectItemPayload) => {
    if (checked) {
      selectMessage(id);
    } else {
      unselectMessage(id);
    }
  };

  const handleSelectAll = () => {
    if (selectedMessageIds.length === filteredMessages?.length) {
      clearMessages();
    } else {
      selectMessages(filteredMessages?.map((message) => message.id) ?? []);
    }
  };

  const isAllSelected = selectedMessageIds.length === filteredMessages?.length;
  const isEmpty = filteredMessages?.length === 0;
  const isAnySelected = selectedMessageIds.length > 0;

  const sidebar = (
    <div className="flex h-full flex-col">
      <div className="border-b px-6 py-4">
        <Input
          type="text"
          placeholder="Search by candidate name..."
          value={searchKeyword}
          autoFocus
          onChange={onChange}
        />
      </div>

      {messagesQuery.isLoading ? (
        <Loader />
      ) : isEmpty ? (
        <CardEmptyState title="No draft messages" />
      ) : (
        <div className="flex flex-1 flex-col overflow-y-auto overscroll-contain">
          {filteredMessages?.map((message) => {
            const { thread } = message;

            return (
              <ThreadListItem
                key={message.id}
                message={{
                  ...message,
                  id: message.id,
                  sentAt: message.sentAt ?? undefined,
                  candidate: {
                    ...thread.candidate,
                  },
                }}
                isHighlighted={highlightedMessageId === message.id}
                isSelected={selectedMessageIds.includes(message.id ?? '')}
                onSelect={handleSelectMessage}
                onClick={() => {
                  setSearchParams({ tab, messageId: message.id });
                }}
              />
            );
          })}
        </div>
      )}
    </div>
  );

  const invalidateDraftMessages = invalidateQuery(
    messagingQueryKeys.messages(DRAFT_MESSAGE_STATUS),
  );

  const content = highlightedMessage?.thread.id ? (
    <>
      <ThreadHeader thread={highlightedMessage.thread} />
      <MessageList
        threadId={highlightedMessage.thread.id}
        messages={highlightedMessage ? [highlightedMessage] : []}
        isLoading={messagesQuery.isLoading}
        onMessageUpdated={invalidateDraftMessages}
        onMessageDeleted={invalidateDraftMessages}
      />
    </>
  ) : (
    <CardEmptyState title="Select a draft to start messaging" />
  );

  return (
    <MessagingLayout
      sidebar={sidebar}
      sidebarTitle="Drafts"
      content={content}
      isAllSelected={isAllSelected}
      isAnySelected={isAnySelected}
      isEmpty={isEmpty}
      onSelectAll={handleSelectAll}
    />
  );
};
